import React from "react";
import ICoBrowseDto, { initialCoBrowseObject } from "../interface/ICoBrowseDto";
import IMessage from "../interface/IMessage";
import { AppContext } from "./genesys.context";
import debugLog from "../utils/debugLog";
import { v4 as uuidv4 } from "uuid";

export interface ICoBrowseContext {
  coBrowseDto: ICoBrowseDto;
  setCoBrowseDto: React.Dispatch<React.SetStateAction<ICoBrowseDto>>;
  startCoBrowsing: () => void;
  declineCoBrowseSession: () => void;
  cobrowseServiceStopSession: () => void;
  allowControl: () => void;
  removeControl: () => void;
  allowNavigation: () => void;
  declineNavigation: () => void;
}

export const CoBrowseContext = React.createContext<ICoBrowseContext>(
  {} as ICoBrowseContext,
);

const makeSystemMessage = (text: string): IMessage => ({
  id: uuidv4(),
  text,
  sender: "system",
  timeStamp: new Date().toISOString(),
  content: null,
  sending: false,
  agent: { avatar: "", name: "", nickname: "" },
});

const CoBrowseContextProvider = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
  const [coBrowseDto, setCoBrowseDto] = React.useState<ICoBrowseDto>(
    initialCoBrowseObject
  );

  const {
    updateMessageList,
  }: {
    updateMessageList: (message: IMessage, saveToLocalStorage: boolean) => void;
  } = React.useContext(AppContext);

  const startCoBrowsing = React.useCallback(() => {
    debugLog("Co-Browsing-Sitzung wird gestartet...");

    updateMessageList(makeSystemMessage("Bildschirmfreigabe wird gestartet..."), true);

    const sessionJoinToken = coBrowseDto.session?.sessionJoinToken;

    Genesys(
      "command",
      "CobrowseService.acceptSession",
      { joinCode: sessionJoinToken },
      () => {
        debugLog("Bildschirmfreigabe erfolgreich angenommen");
        setCoBrowseDto((prev) => ({ ...prev, openOffer: false }));
        updateMessageList(makeSystemMessage("Bildschirmfreigabe angenommen."), true);
      },
      (err: any) => {
        setCoBrowseDto((prev) => ({ ...prev, openOffer: false }));
        updateMessageList(makeSystemMessage("Co-Browsing-Sitzung abgelehnt."), true);
        debugLog(
          "Die Bildschirmfreigabe konnte nicht angenommen werden. Fehlercode:",
          err.code
        );
      }
    );
  }, [coBrowseDto.session?.sessionJoinToken, updateMessageList]);

  const declineCoBrowseSession = React.useCallback(() => {
    Genesys(
      "command",
      "CobrowseService.declineSession",
      {
        joinCode: coBrowseDto.session.sessionJoinToken,
        sessionId: coBrowseDto.session.sessionId,
      },
      () => {
        debugLog("Co-browse session declined");
        setCoBrowseDto({ ...initialCoBrowseObject });
        updateMessageList(makeSystemMessage("Bildschirmfreigabe wurde abgelehnt."), true);
      },
      (err: any) => {
        debugLog("Co-browse session could not be declined:", err);
        setCoBrowseDto({ ...initialCoBrowseObject });
      }
    );
  }, [
    coBrowseDto.session.sessionId,
    coBrowseDto.session.sessionJoinToken,
    updateMessageList,
  ]);

  const cobrowseServiceStopSession = React.useCallback(
    () =>
      Genesys(
        "command",
        "CobrowseService.stopSession",
        {},
        function () {
          debugLog("Co-browse session stopped");
          setCoBrowseDto({ ...initialCoBrowseObject });
          updateMessageList(makeSystemMessage("Die Bildschirmfreigabe wurde beendet."), true);
        },
        function () {
          /* rejected: session may already be ended */
        }
      ),
    [updateMessageList]
  );

  const allowControl = React.useCallback(() => {
    Genesys(
      "command",
      "CobrowseService.acceptControl",
      {},
      function () {
        debugLog("Control accepted");
        setCoBrowseDto((prev) => ({
          ...prev,
          control: { ...prev.control, offer: false, allowControl: true },
        }));
        updateMessageList(makeSystemMessage("Bildschirmkontrolle wurde akzeptiert."), true);
      },
      function () {
        debugLog("Control could not be accepted");
      },
    );
  }, [updateMessageList]);

  const removeControl = React.useCallback(() => {
    Genesys(
      "command",
      "CobrowseService.declineControl",
      {},
      function () {
        setCoBrowseDto((prev) => ({
          ...prev,
          control: { ...prev.control, offer: false, allowControl: false },
        }));
        updateMessageList(makeSystemMessage("Bildschirmkontrolle wurde beendet."), true);
      },
      function () {
        debugLog("Control could not be declined");
      },
    );
  }, [updateMessageList]);

  const allowNavigation = React.useCallback(() => {
    Genesys(
      "command",
      "CobrowseService.acceptNavigation",
      {},
      function () {
        setCoBrowseDto((prev) => ({
          ...prev,
          navigation: { ...prev.navigation, offer: false, allowNavigation: true },
        }));
        updateMessageList(makeSystemMessage("Navigation wurde erlaubt."), true);
      },
      function () {
        debugLog("Navigation not allowed");
      }
    );
  }, [updateMessageList]);

  const declineNavigation = React.useCallback(() => {
    Genesys(
      "command",
      "CobrowseService.declineNavigation",
      {},
      function () {
        setCoBrowseDto((prev) => ({
          ...prev,
          navigation: { ...prev.navigation, offer: false, allowNavigation: false },
        }));
        updateMessageList(makeSystemMessage("Navigation wurde abgelehnt."), true);
      },
      function () {
        debugLog("Navigation not allowed");
      }
    );
  }, [updateMessageList]);

  const value: ICoBrowseContext = React.useMemo(
    () => ({
      coBrowseDto,
      setCoBrowseDto,
      startCoBrowsing,
      declineCoBrowseSession,
      cobrowseServiceStopSession,
      allowControl,
      removeControl,
      allowNavigation,
      declineNavigation,
    }),
    [
      allowControl,
      allowNavigation,
      coBrowseDto,
      cobrowseServiceStopSession,
      declineCoBrowseSession,
      declineNavigation,
      removeControl,
      startCoBrowsing,
    ]
  );

  return (
    <CoBrowseContext.Provider value={value}>
      {children}
    </CoBrowseContext.Provider>
  );
};

export default CoBrowseContextProvider;
